
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { GridLines } from '../grid-lines';
import { ShowViewportControls } from './show-viewport-controls';
import { ShowViewportInfo } from './show-viewport-info';
import { useRefForwarding } from './use-ref-forwarding';
import { useViewportState } from './use-viewport-state';
import { ViewportProvider } from '../../viewport-context';
import { ViewportServiceClass } from '../../viewport-service';
import { contextMenuService } from '../../../../context-menu/context-menu-service';

/**
 * Loading overlay component for viewport initialization
 */
const LoadingOverlay = () => (
	<div style={{
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		zIndex: 10,
		backgroundColor: 'rgba(255, 255, 255, 0.9)',
		padding: '20px',
		borderRadius: '8px',
		textAlign: 'center',
		fontSize: '14px'
	}}>
		<div>Initializing viewport...</div>
		<div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
			Waiting for container dimensions
		</div>
	</div>
);

export interface ViewportControlProps {
	viewportService: ViewportServiceClass;
	children?: React.ReactNode;
	showControls?: boolean;
	showGrid?: boolean;
	showViewportInfo?: boolean;
	backgroundColor?: string;
	className?: string;
	style?: React.CSSProperties;
}

/**
 * ViewportControl Component
 *
 * A simple viewport control component that provides:
 * - SVG canvas with viewport transformations
 * - Grid background (optional)
 * - Viewport controls (zoom, pan, rotation)
 * - Viewport information display
 * - Children rendering inside the viewport
 * - Automatic resize handling
 * - Mouse wheel zoom (instant response, no animation delay)
 *
 * @param viewportService - The ViewportServiceClass instance to control
 * @param children - React nodes to render inside the viewport
 * @param showGrid - Whether to show the grid background
 * @param showControls - Whether to show the viewport controls
 * @param showViewportInfo - Whether to show viewport information
 * @param backgroundColor - Background color for the canvas
 * @param gridColor - Color for the grid lines
 * @param className - Additional CSS classes
 * @param style - Additional inline styles
 * @param ref - Forwarded ref that will be attached to the SVG element
 */
export const ViewportControl = React.memo(forwardRef<SVGSVGElement, ViewportControlProps>((props, ref) => {
	const { viewportService } = props;

	// Handle null viewportService gracefully
	if (!viewportService) {
		return (
			<div style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: '#f0f0f0',
				fontFamily: 'monospace'
			}}>
				<div>Viewport service not available</div>
			</div>
		);
	}

	return <ViewportControlContents {...props} ref={ref} />;
}));

const ViewportControlContents = React.memo(forwardRef<SVGSVGElement, ViewportControlProps>((props, ref) => {
	const {
		viewportService,
		children,
		showControls = viewportService.config.showControls,
		showGrid = viewportService.config.showGrid,
		showViewportInfo = viewportService.config.showViewportInfo,
		backgroundColor = viewportService.config.backgroundColor,
		className = '',
		style = {}
	} = props;

	const containerRef = useRef<HTMLDivElement>(null);
	const svgRef = useRefForwarding(ref);

	const [isResizing, setIsResizing] = useState(false);
	const [autoLevelText, setAutoLevelText] = useState(true);
	const [isContainerInitialized, setIsContainerInitialized] = useState(false);
	const hasRegisteredSvg = useRef(false); // Track if SVG has been registered


	// Reset initialization state when viewport service changes (e.g., controller switch)
	useEffect(() => {
		console.log('ViewportControl: Viewport service changed, resetting initialization state');
		setIsContainerInitialized(false);
		hasRegisteredSvg.current = false;
		
		// Get actions from the viewport service
		const serviceActions = viewportService.actions;
		
		// Reset the viewport service size state to force re-initialization
		serviceActions.resetSizeState();
		
		// Force re-detection of container size if we have a valid container ref
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) {
				console.log('ViewportControl: Re-detecting container size after service change:', rect);
				serviceActions.updateSize(rect.width, rect.height);
				setIsContainerInitialized(true);
			}
		}
	}, [viewportService]);

	// Get viewport state using custom hook
	const { viewportState, containerSize, viewerSize, viewBox } = useViewportState(viewportService);

	// Get actions and config directly from service
	const { actions, config } = viewportService;


	// Get getViewBox function from service
	// const getViewBox = useCallback(() => viewportService.getViewBox(), [viewportService]);

	// Callback ref to detect when container is actually rendered
	const setContainerRef = useCallback((node: HTMLDivElement | null) => {
		console.log('ViewportControl: setContainerRef called with:', {
			node,
			hasNode: Boolean(node),
			nodeDimensions: node
			? {
				clientWidth: node.clientWidth,
				clientHeight: node.clientHeight,
				getBoundingClientRect: node.getBoundingClientRect()
			}
			: null
		});

		containerRef.current = node;
		if (node) {
			// Set size immediately when container is rendered
			const rect = node.getBoundingClientRect();
			console.log('ViewportControl: Container ref set, dimensions:', rect);

			// Only update size if we have valid dimensions
			if (rect.width > 0 && rect.height > 0) {
				try {
					// Check if size actually changed to prevent infinite loops
					const currentSize = viewportService.containerSize;
					if (currentSize.width !== rect.width || currentSize.height !== rect.height) {
						console.log('ViewportControl: Initial container size update:', {
							old: currentSize,
							new: { width: rect.width, height: rect.height }
						});
						actions.updateSize(rect.width, rect.height);
						// Mark container as initialized after first successful size update
						setIsContainerInitialized(true);
					}
				} catch (error) {
					console.warn('Initial container size update failed:', error);
				}
			} else {
				console.warn('ViewportControl: Container has zero dimensions:', rect);
				// If dimensions are not ready, schedule a retry
				requestAnimationFrame(() => {
					if (node && containerRef.current === node) {
						const retryRect = node.getBoundingClientRect();
						console.log('ViewportControl: Retry container dimensions:', retryRect);
						if (retryRect.width > 0 && retryRect.height > 0) {
							try {
								// Check if size actually changed to prevent infinite loops
								const currentSize = viewportService.containerSize;
								if (currentSize.width !== retryRect.width || currentSize.height !== retryRect.height) {
									console.log('ViewportControl: Retry container size update:', {
										old: currentSize,
										new: { width: retryRect.width, height: retryRect.height }
									});
									actions.updateSize(retryRect.width, retryRect.height);
									setIsContainerInitialized(true);
								}
							} catch (error) {
								console.warn('Retry container size update failed:', error);
							}
						}
					}
				});
			}
		}
	}, []); // Remove viewportService.containerSize dependency to prevent circular updates

	// Handle resize functionality
	const handleResize = useCallback(() => {
		if (containerRef.current && actions && typeof actions.updateSize === 'function') {
			try {
				const containerWidth = containerRef.current.clientWidth;
				const containerHeight = containerRef.current.clientHeight;

				// Only update size if we have valid dimensions
				if (containerWidth > 0 && containerHeight > 0) {
					// Check if size actually changed significantly to prevent infinite loops
					const currentSize = viewportService.containerSize;
					const widthDiff = Math.abs(currentSize.width - containerWidth);
					const heightDiff = Math.abs(currentSize.height - containerHeight);

					// Only update if change is significant (more than 1px) to prevent micro-updates
					if (widthDiff > 1 || heightDiff > 1) {
						// Update viewport service with new container size
						actions.updateSize(containerWidth, containerHeight);
					}
				}
			} catch (error) {
				console.warn('Resize update failed:', error);
			}
		}
	}, [actions, viewportService.containerSize]); // Re-add dependency but with better change detection

	// Effect for handling window resize
	useEffect(() => {
		const handleWindowResize = () => {
			setIsResizing(true);
			handleResize();
			// Debounce resize indicator
			setTimeout(() => setIsResizing(false), 100);
		};

		window.addEventListener('resize', handleWindowResize);
		return () => window.removeEventListener('resize', handleWindowResize);
	}, [handleResize]);

	// Effect for initializing container size when component mounts
	useEffect(() => {
		// Set size immediately when component mounts
		handleResize();

		// Also set a small delay as backup to ensure DOM is fully ready
		const timer = setTimeout(() => {
			handleResize();
		}, 50);

		// Add ResizeObserver to detect container size changes
		// FIXED: Added debouncing and significant change detection to prevent infinite loops
		// where every viewport action would trigger ResizeObserver, causing performance issues
		let resizeObserver: any = null;
		let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
		if (containerRef.current && typeof window !== 'undefined' && 'ResizeObserver' in window) {
			const ResizeObserverClass = (window as any).ResizeObserver;

			// Debounce resize observer to prevent excessive firing
			const debouncedResizeHandler = (entries: any[]) => {
				// Clear any existing timeout
				if (resizeTimeout) {
					clearTimeout(resizeTimeout);
				}

				// Set a new timeout to debounce the resize handling
				resizeTimeout = setTimeout(() => {
					for (const entry of entries) {
						const { width, height } = entry.contentRect;

						if (width > 0 && height > 0 && actions && typeof actions.updateSize === 'function') {
							const currentSize = viewportService.containerSize;

							// Only update if size actually changed significantly (more than 1px to prevent micro-changes)
							const widthDiff = Math.abs(currentSize.width - width);
							const heightDiff = Math.abs(currentSize.height - height);

							if (widthDiff > 1 || heightDiff > 1) {
								actions.updateSize(width, height);
								// Container now has valid dimensions — mark as initialized
								// so children render and loading overlay is removed.
								// This handles containers that start hidden (e.g. inside a Collapse).
								setIsContainerInitialized(true);
							}
						}
					}
				}, 100); // 100ms debounce delay
			};

			resizeObserver = new ResizeObserverClass(debouncedResizeHandler);
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			clearTimeout(timer);
			if (resizeTimeout) {
				clearTimeout(resizeTimeout);
			}
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, []); // Remove handleResize dependency to prevent infinite loops

	// Effect for registering SVG element with viewport service
	useEffect(() => {
		// debugger;
		// Only register if we have an SVG element and haven't registered yet
		if (svgRef.current && actions && typeof actions.registerSvg === 'function' && !hasRegisteredSvg.current) {
			actions.registerSvg(svgRef.current);
			hasRegisteredSvg.current = true; // Mark as registered
			actions.triggerAutoZoom();
		}
	}, [svgRef.current]); // Only depend on SVG ref, actions is always available

	// Cleanup effect for unregistering SVG when component unmounts
	useEffect(() => {
		return () => {
			// Unregister on unmount
			if (actions && typeof actions.unregisterSvg === 'function') {
				actions.unregisterSvg();
				hasRegisteredSvg.current = false; // Reset registration flag
			}
		};
	}, []); // Empty dependency array - only run on unmount

	// Handle mouse wheel zoom - uses setZoom for instant response (no animation delay)
	// Note: We use a ref to store the handler so it has access to current state
	const wheelHandlerRef = useRef<((event: WheelEvent) => void) | undefined>(undefined);

	// Update the wheel handler ref whenever dependencies change
	useEffect(() => {
		wheelHandlerRef.current = (event: WheelEvent) => {
			// Prevent default browser scrolling behavior
			event.preventDefault();

			if (!actions || typeof actions.setZoom !== 'function') {
				return;
			}

			// Get current zoom from viewport state
			const currentZoom = viewportState.zoom;
			const { minZoom, maxZoom, zoomStep } = config;

			// Calculate zoom delta based on wheel direction
			// deltaY < 0 = scroll up = zoom in, deltaY > 0 = scroll down = zoom out
			// Use zoomStep for consistent increments, scaled by wheel delta magnitude
			const zoomMultiplier = Math.abs(event.deltaY) > 100 ? 2 : 1; // Faster zoom for larger wheel movements
			const zoomDelta = event.deltaY < 0 ? zoomStep * zoomMultiplier : -zoomStep * zoomMultiplier;

			// Calculate new zoom level with constraints
			const newZoom = Math.max(minZoom, Math.min(maxZoom, currentZoom + zoomDelta));

			// Apply zoom instantly using setZoom (not smoothZoom) for immediate response
			actions.setZoom(newZoom);
		};
	}, [actions, viewportState.zoom, config]);

	// Attach native wheel event listener with { passive: false } to allow preventDefault
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleWheel = (event: WheelEvent) => {
			wheelHandlerRef.current?.(event);
		};

		// Add listener with passive: false to allow preventDefault
		container.addEventListener('wheel', handleWheel, { passive: false });

		return () => {
			container.removeEventListener('wheel', handleWheel);
		};
	}, []);

	// Middle-click pan - track current pan position and commit on release
	const panState = useRef<{
		isPanning: boolean;
		isShiftPan: boolean;
		startMouseX: number;
		startMouseY: number;
		startViewBoxX: number;
		startViewBoxY: number;
		viewBoxWidth: number;
		viewBoxHeight: number;
		startPanX: number;
		startPanY: number;
		lastDeltaX: number;
		lastDeltaY: number;
	} | null>(null);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const commitPan = () => {
			if (panState.current && panState.current.isPanning) {
				const { startPanX, startPanY, lastDeltaX, lastDeltaY } = panState.current;

				// Calculate final pan by adding scaled delta to original pan values
				const newPanX = startPanX + lastDeltaX;
				const newPanY = startPanY + lastDeltaY;

				panState.current.isPanning = false;
				panState.current = null;

				// Commit final position (only if valid numbers)
				if (!isNaN(newPanX) && !isNaN(newPanY) && isFinite(newPanX) && isFinite(newPanY)) {
					viewportService.actions.setPan(newPanX, newPanY);
				}
			}
		};

		const handleMouseDown = (event: MouseEvent) => {
			const isMiddleClick = event.button === 1 && config.middleClickPanEnabled;
			const isShiftLeftClick = event.button === 0 && event.shiftKey;
			if (!isMiddleClick && !isShiftLeftClick) return;
			if (!config.enablePan) return;

			const svg = svgRef.current;
			if (!svg) return;

			// Parse current viewBox from SVG (handle both space and comma separators)
			const currentViewBox = svg.getAttribute('viewBox');
			if (!currentViewBox) return;

			const parts = currentViewBox.split(/[\s,]+/).map(Number);
			if (parts.length < 4 || parts.some(isNaN)) return;

			const [vbX, vbY, vbW, vbH] = parts;

			event.preventDefault();

			panState.current = {
				isPanning: true,
				isShiftPan: isShiftLeftClick,
				startMouseX: event.clientX,
				startMouseY: event.clientY,
				startViewBoxX: vbX,
				startViewBoxY: vbY,
				viewBoxWidth: vbW,
				viewBoxHeight: vbH,
				startPanX: viewportService.state.xPan,
				startPanY: viewportService.state.yPan,
				lastDeltaX: 0,
				lastDeltaY: 0
			};
		};

		const handleMouseMove = (event: MouseEvent) => {
			if (!panState.current?.isPanning) return;

			const svg = svgRef.current;
			if (!svg) return;

			const mouseDeltaX = event.clientX - panState.current.startMouseX;
			const mouseDeltaY = event.clientY - panState.current.startMouseY;

			const zoom = viewportService.state.zoom || 1;
			const baseSensitivity = config.middleClickPanSensitivity || 0.5;
			const sensitivity = panState.current.isShiftPan ? baseSensitivity * 0.5 : baseSensitivity;

			// Calculate scaled delta for pan values (positive = move content in mouse direction)
			const scaledDeltaX = (mouseDeltaX / zoom) * sensitivity;
			const scaledDeltaY = (mouseDeltaY / zoom) * sensitivity;

			// Store the delta for commit
			panState.current.lastDeltaX = scaledDeltaX;
			panState.current.lastDeltaY = scaledDeltaY;

			// Calculate new viewBox position (subtract delta because viewBox moves opposite to content)
			const newVbX = panState.current.startViewBoxX - scaledDeltaX;
			const newVbY = panState.current.startViewBoxY - scaledDeltaY;

			// Validate before updating
			if (isNaN(newVbX) || isNaN(newVbY) || !isFinite(newVbX) || !isFinite(newVbY)) return;

			// Directly update SVG viewBox
			const { viewBoxWidth, viewBoxHeight } = panState.current;
			if (isNaN(viewBoxWidth) || isNaN(viewBoxHeight)) return;

			const newViewBox = `${newVbX} ${newVbY} ${viewBoxWidth} ${viewBoxHeight}`;
			svg.setAttribute('viewBox', newViewBox);
		};

		const handleMouseUp = (event: MouseEvent) => {
			if (event.button !== 1 && event.button !== 0) return;
			commitPan();
		};

		const handleKeyUp = (event: KeyboardEvent) => {
			if (event.key === 'Shift') commitPan();
		};

		const handleMouseLeave = () => {
			commitPan();
		};

		const handleBlur = () => {
			commitPan();
		};

		container.addEventListener('mousedown', handleMouseDown);
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		window.addEventListener('keyup', handleKeyUp);
		document.addEventListener('mouseleave', handleMouseLeave);
		window.addEventListener('blur', handleBlur);

		return () => {
			container.removeEventListener('mousedown', handleMouseDown);
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
			window.removeEventListener('keyup', handleKeyUp);
			document.removeEventListener('mouseleave', handleMouseLeave);
			window.removeEventListener('blur', handleBlur);
		};
	}, [config.middleClickPanEnabled, config.enablePan, config.middleClickPanSensitivity, viewportService]);

	// Get current viewBox with comprehensive safety check
	// const viewBox = viewportService.getViewBox();

	// If viewBox is null, the service is still initializing
	const content = viewBox
		? <svg
			ref={svgRef}
			viewBox={viewBox}
			xmlns="http://www.w3.org/2000/svg"
			style={{ width: '100%', height: '100%' }}
		>
			{ showGrid &&
				<g ransform-origin={'0 0'} transform={`rotate(${config.rotation})`}>
					<GridLines diameter={viewerSize?.diameter || 0}/>
				</g>
			}

			<g id="viewport-content-group">
				{ /* circle for test with no content */ }
				{/* <circle cx="36" cy="60" r="60" fill="red" opacity=".1"/> */}

				{/* Children rendered inside the viewport - only when initialized */}
				{isContainerInitialized && children}
			</g>
		</svg>
		: <div style={{
			width: '100%',
			height: '100%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: '#f0f0f0'
			// fontFamily: 'monospace'
		}}>
			<div>Viewport initializing...</div>
			<div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
				Waiting for container dimensions
			</div>
		</div>;

	return (
		<ViewportProvider viewportService={viewportService}>
			<div className={`viewport-control ${className}`}
				style={{
					width: '100%',
					height: '100%',
					position: 'relative',
					overflow: 'hidden',
					// fontFamily: 'monospace',
					...style
				}}
			>
				{/* SVG Canvas as Background - Always rendered for initialization */}
				<div ref={setContainerRef}
					style={{
						width: '100%',
						height: '100%',
						position: 'absolute',
						top: 0,
						left: 0,
						backgroundColor,
						zIndex: 1
					}}
				>
					{content}
					{/* Loading overlay - shown while initializing */}
					{!isContainerInitialized && <LoadingOverlay />}
				</div>

				{/* Viewport Controls - Top Left */}
				{showControls && <ShowViewportControls
					viewportService={viewportService}
					actions={actions}
					config={config}
					autoLevelText={autoLevelText}
					setAutoLevelText={setAutoLevelText}
				/>}

				{/* Viewport Information - Top Right */}
				{showViewportInfo && <ShowViewportInfo
					viewportService={viewportService}
				/>}

				{/* Resize Indicator */}
				{isResizing && (
					<div style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						zIndex: 20,
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						color: 'white',
						padding: '8px 16px',
						borderRadius: '20px',
						fontSize: '14px',
						fontWeight: 'bold'
					}}>
						Resizing...
					</div>
				)}
			</div>
		</ViewportProvider>
	);
}));
